import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Chip,
  Tooltip
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

/**
 * ProgressIndicator Component
 * Compact progress indicator for showing build status
 */
const ProgressIndicator = ({ progress, compact = false, currentStep = null }) => {
  const { completed, total, pending, errors } = progress;
  const progressPercent = (completed / total) * 100;

  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 100 }}>
          <LinearProgress 
            variant="determinate" 
            value={progressPercent}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 50 }}>
          {completed}/{total}
        </Typography>
        {currentStep && (
          <Chip 
            label={`Building: ${currentStep}`}
            size="small"
            color="primary"
            sx={{ 
              height: 20, 
              fontSize: '0.7rem',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 }
              }
            }}
          />
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Build Progress
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {completed > 0 && (
            <Tooltip title="Completed">
              <Chip 
                icon={<CheckIcon />}
                label={completed}
                size="small"
                color="success"
                sx={{ height: 24 }}
              />
            </Tooltip>
          )}
          {pending > 0 && (
            <Tooltip title="Pending">
              <Chip 
                icon={<PendingIcon />}
                label={pending}
                size="small"
                color="default"
                sx={{ height: 24 }}
              />
            </Tooltip>
          )}
          {errors > 0 && (
            <Tooltip title="Errors">
              <Chip 
                icon={<ErrorIcon />}
                label={errors}
                size="small"
                color="error"
                sx={{ height: 24 }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>

      <LinearProgress 
        variant="determinate" 
        value={progressPercent}
        sx={{ 
          height: 8, 
          borderRadius: 4,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: errors > 0 ? '#f44336' : '#4caf50'
          }
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Typography 
          variant="caption" 
          color="text.secondary"
        >
          {Math.round(progressPercent)}% Complete
        </Typography>
        {currentStep && (
          <Chip 
            label={`🔨 ${currentStep}`}
            size="small"
            color="primary"
            sx={{ 
              height: 20, 
              fontSize: '0.7rem',
              animation: 'pulse 1.5s ease-in-out infinite',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.7 }
              }
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default ProgressIndicator;

